import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  FormControl,
  MenuItem,
  Select,
  useMediaQuery,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { useFormik } from "formik";
import Sidebar from "./CourseSidebar";
import color from "../../../../components/utils/Colors";
import { useNavigate, useLocation } from "react-router-dom";
import { createCourseValidations } from "../../../../components/schema/schema";
import {
  courseCategory,
  languages,
  skillLevels,
} from "../../../../components/utils/Data";
import {
  updateCourseLandingPage,
  uploadCourseCoverImage,
  uploadCourseShortVideo,
  getOneDataFromTable,
} from "../../../../services/services";
import { useSnackbar } from "../../../../components/utils/SnackBar";

const CourseLandingPage = () => {
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const showSnackbar = useSnackbar();

  const courseId = (location.state as any)?.courseId;
  const formik = useFormik({
    initialValues: {
      title: "",
      subtitle: "",
      description: "",
      category: "",
      level: "",
      language: "",
      coverImage: null as File | null,
      shortVideo: null as File | null,
    },
    validationSchema: createCourseValidations,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        console.log("Course ID", courseId);
        await updateCourseLandingPage(courseId, {
          title: values.title,
          subTitle: values.subtitle,
          description: values.description,
          category: values.category,
          level: values.level,
          language: values.language,
        });

        if (values.coverImage) {
          const imgData = new FormData();
          imgData.append("coverImage", values.coverImage);
          await uploadCourseCoverImage(courseId, imgData);
        }

        if (values.shortVideo) {
          const videoData = new FormData();
          videoData.append("shortVideo", values.shortVideo);
          await uploadCourseShortVideo(courseId, videoData);
        }

        // Always carry courseId to the next step
        navigate("/course-price", { state: { courseId } });
      } catch (err: any) {
        const msg =
          err.response?.data?.message ||
          "An error occurred while updating the landing page.";
        showSnackbar(msg, "error");
        console.error("Course landing page update error:", err);
      } finally {
        setLoading(false);
      }
    },
    enableReinitialize: true,
  });

  useEffect(() => {
    const fetchCourseData = async () => {
      if (courseId) {
        try {
          const res = await getOneDataFromTable("course", courseId);
          if (res?.data?.data) {
            const courseData = res.data.data;
            formik.setValues({
              title: courseData.title || "",
              subtitle: courseData.subTitle || "",
              description: courseData.description || "",
              category: courseData.category || "",
              level: courseData.level || "",
              language: courseData.language || "",
              coverImage: null,
              shortVideo: null,
            });
          }
        } catch (error) {
          console.error("Failed to fetch course details:", error);
          showSnackbar("Failed to fetch course details", "error");
        }
      }
    };
    fetchCourseData();
  }, [courseId]);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
  ) => {
    formik.setFieldValue(fieldName, e.currentTarget.files?.[0] || null);
  };

  const handlePreview = async () => {
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors).length > 0) {
      formik.setTouched(
        Object.keys(formik.values).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {},
        ),
      );
      return;
    }
    setLoading(true);
    try {
      await updateCourseLandingPage(courseId, {
        title: formik.values.title,
        subTitle: formik.values.subtitle,
        description: formik.values.description,
        category: formik.values.category,
        level: formik.values.level,
        language: formik.values.language,
      });

      if (formik.values.coverImage) {
        const imgData = new FormData();
        imgData.append("coverImage", formik.values.coverImage);
        await uploadCourseCoverImage(courseId, imgData);
      }

      if (formik.values.shortVideo) {
        const videoData = new FormData();
        videoData.append("shortVideo", formik.values.shortVideo);
        await uploadCourseShortVideo(courseId, videoData);
      }

      navigate("/course-preview", { state: { courseId } });
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        "An error occurred while saving. Please try again.";
      showSnackbar(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "auto",
        margin: isSmallScreen ? "10px" : "30px",
        marginTop: "0px",
        paddingTop: isSmallScreen ? "5px" : "30px",
        gap: "1%",
        marginBottom: "0px",
        paddingBottom: "5%",
        borderRadius: "10px",
        flexDirection: isSmallScreen ? "column" : "row",
      }}
    >
      {/* <Sidebar /> */}

      <Box
        sx={{
          marginTop: isSmallScreen ? "25px" : "0px",
          flex: 1,
          pl: "3%",
          borderRadius: "16px",
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            background: color.textColor,
            textAlign: "center",
            color: "white",
            width: "fit-content",
            margin: "auto",
            padding: "2px 10px",
            borderRadius: "4px",
            mb: 2,
          }}
        >
          Landing Page Content
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          <Typography
            sx={{
              color: color.textColor,
              ml: 1,
              fontSize: "20px",
              fontWeight: "bold",
              mb: -1,
            }}
          >
            Course Title
          </Typography>
          <TextField
            className="input-root2"
            fullWidth
            margin="normal"
            id="title"
            name="title"
            placeholder="Enter Course Title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
          />

          <Typography
            sx={{
              color: color.textColor,
              ml: 1,
              fontSize: "20px",
              fontWeight: "bold",
              mb: -1,
              mt: 1,
            }}
          >
            Sub-title
          </Typography>
          <TextField
            className="input-root2"
            fullWidth
            margin="normal"
            id="subtitle"
            name="subtitle"
            placeholder="Enter Sub-title"
            value={formik.values.subtitle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />

          <Typography
            sx={{
              color: color.textColor,
              ml: 1,
              fontSize: "20px",
              fontWeight: "bold",
              mb: -1,
              mt: 1,
            }}
          >
            Course Description
          </Typography>
          <TextField
            className="input-root2"
            fullWidth
            margin="normal"
            id="description"
            name="description"
            placeholder="Enter Course Description"
            multiline
            rows={4}
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={formik.touched.description && formik.errors.description}
          />

          <Typography
            sx={{
              color: color.textColor,
              ml: 1,
              fontSize: "20px",
              fontWeight: "bold",
              mb: -1,
              mt: 1,
            }}
          >
            Other Info
          </Typography>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} sm={4}>
              <FormControl
                className="input-root2"
                fullWidth
                error={
                  formik.touched.category && Boolean(formik.errors.category)
                }
              >
                <Select
                  id="category"
                  name="category"
                  value={formik.values.category}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Category
                  </MenuItem>
                  {(courseCategory as any[]).map((cat) => {
                    const val = typeof cat === "object" ? cat.value : cat;
                    const label = typeof cat === "object" ? cat.label : cat;
                    return (
                      <MenuItem key={val} value={val}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
                {formik.touched.category && formik.errors.category && (
                  <FormHelperText>{formik.errors.category}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl
                className="input-root2"
                fullWidth
                error={formik.touched.level && Boolean(formik.errors.level)}
              >
                <Select
                  id="level"
                  name="level"
                  value={formik.values.level}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Level
                  </MenuItem>
                  {(skillLevels as any[]).map((level) => {
                    const val = typeof level === "object" ? level.value : level;
                    const label =
                      typeof level === "object" ? level.label : level;
                    return (
                      <MenuItem key={val} value={val}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
                {formik.touched.level && formik.errors.level && (
                  <FormHelperText>{formik.errors.level}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl
                className="input-root2"
                fullWidth
                error={
                  formik.touched.language && Boolean(formik.errors.language)
                }
              >
                <Select
                  id="language"
                  name="language"
                  value={formik.values.language}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Language
                  </MenuItem>
                  {(languages as any[]).map((lang) => {
                    const val = typeof lang === "object" ? lang.value : lang;
                    const label = typeof lang === "object" ? lang.label : lang;
                    return (
                      <MenuItem key={val} value={val}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
                {formik.touched.language && formik.errors.language && (
                  <FormHelperText>{formik.errors.language}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          <Box mt={5} pl={1}>
            <Typography
              variant="h6"
              sx={{
                color: color.textColor,
                fontSize: "20px",
                fontWeight: "bold",
              }}
            >
              Course Cover Page
            </Typography>
            <Typography variant="body2" style={{ color: color.textColor }}>
              Upload your course image here (JPG, PNG, JPEG)
            </Typography>
            <input
              accept="image/*"
              id="coverImage"
              name="coverImage"
              type="file"
              onChange={(e) => handleFileChange(e, "coverImage")}
            />
            {formik.touched.coverImage && formik.errors.coverImage && (
              <FormHelperText style={{ color: "#d32f2f" }}>
                {formik.errors.coverImage as string}
              </FormHelperText>
            )}
          </Box>

          <Box mt={5}>
            <Typography
              variant="h6"
              sx={{
                color: color.textColor,
                fontSize: "20px",
                fontWeight: "bold",
              }}
            >
              Course Short Video
            </Typography>
            <Typography variant="body2" style={{ color: color.textColor }}>
              Upload your course preview video (clear quality)
            </Typography>
            <input
              accept="video/*"
              id="shortVideo"
              name="shortVideo"
              type="file"
              onChange={(e) => handleFileChange(e, "shortVideo")}
            />
            {formik.touched.shortVideo && formik.errors.shortVideo && (
              <FormHelperText style={{ color: "#d32f2f" }}>
                {formik.errors.shortVideo as string}
              </FormHelperText>
            )}
          </Box>

          <Grid container justifyContent="space-between" mt={6}>
            <Button
              id="button"
              type="button"
              disabled={loading}
              onClick={handlePreview}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} />
                  Saving…
                </Box>
              ) : (
                "Save & Preview"
              )}
            </Button>

            <Button id="button" type="submit" disabled={loading}>
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} />
                  Saving…
                </Box>
              ) : (
                "Save & Next"
              )}
            </Button>
          </Grid>
        </form>
      </Box>
    </Box>
  );
};
export default CourseLandingPage;
